import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/** @type {Promise<Array<any>> | null} */
let mugsProductsPromise = null;

/**
 * Compact cart drawer cross-sell for same-design / same-niche mugs.
 *
 * Liquid renders the first fast candidate from `collections['mugs']`; this
 * element upgrades that result via `/collections/mugs/products.json?limit=250`
 * when needed because Liquid collection loops are capped and the mug catalog is
 * already larger than one Liquid page.
 */
class CartDrawerUpsell extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.addEventListener('click', this.#handleClick, { signal: this.#abortController.signal });

    if (this.dataset.liquidReason !== 'same_design') {
      this.#upgradeFromMugsCollection();
    }
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  async #upgradeFromMugsCollection() {
    const sourceLines = this.#sourceLines;
    if (!sourceLines.length || !this.dataset.mugsUrl) return;

    const card = this.#card;
    const hasLiquidCard = Boolean(card?.dataset.variantId);

    try {
      const mugs = await this.#fetchMugs();
      const selected = this.#selectUpsell(mugs, sourceLines);

      if (selected) {
        this.#renderCard(selected);
      } else if (!hasLiquidCard) {
        card?.setAttribute('hidden', '');
      }
    } catch (error) {
      // Keep the Liquid-rendered candidate when the enhancement request fails.
      if (!hasLiquidCard) card?.setAttribute('hidden', '');
      console.error('Cart drawer upsell lookup failed', error);
    }
  }

  /**
   * @returns {Promise<Array<any>>}
   */
  async #fetchMugs() {
    if (!mugsProductsPromise) {
      mugsProductsPromise = fetch(this.dataset.mugsUrl || '', {
        headers: { Accept: 'application/json' },
      })
        .then((response) => {
          if (!response.ok) throw new Error(`Mug lookup failed: ${response.status}`);
          return response.json();
        })
        .then((data) => (Array.isArray(data?.products) ? data.products : []));
    }

    return mugsProductsPromise;
  }

  /**
   * @param {Array<any>} products
   * @param {Array<any>} sourceLines
   */
  #selectUpsell(products, sourceLines) {
    const cartProductIds = new Set(this.#cartLines.map((line) => line.product_id?.toString()).filter(Boolean));
    const availableMugs = products.filter((product) => {
      const productId = product?.id?.toString();
      return (
        productId &&
        !cartProductIds.has(productId) &&
        this.#productType(product) === 'mug' &&
        this.#availableVariant(product)
      );
    });

    for (const line of sourceLines) {
      const designId8 = this.#designId8(line.tags);
      if (!designId8) continue;

      const designTag = `design:${designId8}`;
      const product = availableMugs.find((mug) => this.#hasTag(mug, designTag));
      if (product) {
        return this.#productPayload(product, {
          reason: 'same_design',
          designId8,
          fromProduct: line.title,
          nicheTag: '',
        });
      }
    }

    for (const line of sourceLines) {
      const designId8 = this.#designId8(line.tags);
      const nicheTags = this.#nicheTags(line.tags);

      for (const nicheTag of nicheTags) {
        const product = availableMugs.find((mug) => this.#hasTag(mug, nicheTag));
        if (product) {
          return this.#productPayload(product, {
            reason: 'niche',
            designId8,
            fromProduct: line.title,
            nicheTag,
          });
        }
      }
    }

    return null;
  }

  /**
   * @param {any} product
   * @param {{ reason: string, designId8: string, fromProduct: string, nicheTag: string }} match
   */
  #productPayload(product, match) {
    const variant = this.#availableVariant(product);
    if (!variant) return null;

    return {
      productId: product.id?.toString() || '',
      variantId: variant.id?.toString() || '',
      title: product.title || '',
      url: product.handle ? `/products/${product.handle}` : product.url || '#',
      image: product.images?.[0]?.src || product.image?.src || '',
      price: variant.price || product.variants?.[0]?.price || '16.99',
      ...match,
    };
  }

  /** @param {any} product */
  #availableVariant(product) {
    const variants = Array.isArray(product?.variants) ? product.variants : [];
    return variants.find((variant) => variant.available !== false) || variants[0] || null;
  }

  /** @param {any} product */
  #productType(product) {
    return (product?.product_type || product?.type || '').toString().trim().toLowerCase();
  }

  /** @param {any} product */
  #productTags(product) {
    const tags = product?.tags;
    if (Array.isArray(tags)) return tags.map((tag) => tag.toString().trim()).filter(Boolean);
    if (typeof tags === 'string') return tags.split(',').map((tag) => tag.trim()).filter(Boolean);
    return [];
  }

  /** @param {any} product */
  #hasTag(product, wantedTag) {
    const wanted = wantedTag.toString().trim().toLowerCase();
    return this.#productTags(product).some((tag) => tag.toLowerCase() === wanted);
  }

  /** @param {Array<string>} tags */
  #designId8(tags) {
    const designTag = this.#normalizeTags(tags).find((tag) => tag.toLowerCase().startsWith('design:'));
    return designTag ? designTag.slice(7).trim() : '';
  }

  /** @param {Array<string>} tags */
  #nicheTags(tags) {
    const excludedTags = new Set(['mug', 'tee', 't-shirt', 'shirt']);

    return this.#normalizeTags(tags).filter((tag) => {
      const normalized = tag.toLowerCase();
      return (
        tag &&
        !normalized.startsWith('design:') &&
        !excludedTags.has(normalized) &&
        !normalized.startsWith('color:') &&
        !normalized.startsWith('size:')
      );
    });
  }

  /** @param {Array<string> | string | undefined} tags */
  #normalizeTags(tags) {
    if (Array.isArray(tags)) return tags.map((tag) => tag.toString().trim()).filter(Boolean);
    if (typeof tags === 'string') return tags.split(',').map((tag) => tag.trim()).filter(Boolean);
    return [];
  }

  /** @param {any} product */
  #renderCard(product) {
    const card = this.#card;
    if (!card || !product?.variantId) return;

    card.dataset.productId = product.productId;
    card.dataset.variantId = product.variantId;
    card.dataset.designId8 = product.designId8 || '';
    card.dataset.reason = product.reason || '';
    card.dataset.fromProduct = product.fromProduct || '';
    card.dataset.nicheTag = product.nicheTag || '';

    const imageLink = document.createElement('a');
    imageLink.className = 'cart-drawer-upsell__image-link';
    imageLink.href = product.url;
    imageLink.tabIndex = -1;
    imageLink.setAttribute('aria-hidden', 'true');

    if (product.image) {
      const image = document.createElement('img');
      image.className = 'cart-drawer-upsell__image';
      image.src = product.image;
      image.alt = '';
      image.loading = 'lazy';
      image.width = 72;
      image.height = 72;
      imageLink.append(image);
    }

    const body = document.createElement('div');
    body.className = 'cart-drawer-upsell__body';

    const eyebrow = document.createElement('p');
    eyebrow.className = 'cart-drawer-upsell__eyebrow';
    eyebrow.textContent =
      product.reason === 'same_design'
        ? 'Complete the set — add the matching mug'
        : 'Complete the set — add a niche mug';

    const title = document.createElement('a');
    title.className = 'cart-drawer-upsell__title';
    title.href = product.url;
    title.textContent = product.title;

    const meta = document.createElement('div');
    meta.className = 'cart-drawer-upsell__meta';

    const price = document.createElement('span');
    price.className = 'cart-drawer-upsell__price';
    price.textContent = this.#formatPrice(product.price);
    meta.append(price);

    const cartTotal = Number(this.dataset.cartTotal || 0);
    if (cartTotal >= 2500 && cartTotal < 3500) {
      const nudge = document.createElement('span');
      nudge.className = 'cart-drawer-upsell__nudge';
      nudge.textContent = 'Adds Shop Pay installments (orders $35+)';
      meta.append(nudge);
    }

    body.append(eyebrow, title, meta);

    const addButton = document.createElement('button');
    addButton.className = 'cart-drawer-upsell__add';
    addButton.type = 'button';
    addButton.dataset.upsellAdd = '';
    addButton.textContent = 'Add';

    card.replaceChildren(imageLink, body, addButton);
    card.hidden = false;
  }

  /** @param {string | number} price */
  #formatPrice(price) {
    const amount = Number(price);
    if (!Number.isFinite(amount)) return '$16.99';

    try {
      const currency = window.Shopify?.currency?.active || 'USD';
      return new Intl.NumberFormat(undefined, { style: 'currency', currency }).format(amount);
    } catch (_error) {
      return `$${amount.toFixed(2)}`;
    }
  }

  /** @param {MouseEvent} event */
  #handleClick = async (event) => {
    const button = event.target instanceof Element ? event.target.closest('[data-upsell-add]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const card = this.#card;
    const variantId = card?.dataset.variantId;
    if (!card || !variantId) return;

    const originalText = button.textContent || 'Add';
    button.disabled = true;
    button.textContent = 'Adding…';
    this.#statusText = '';

    try {
      const sections = this.#cartSectionIds;
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          items: [{ id: Number(variantId), quantity: 1 }],
          sections: sections.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const added = await response.json();
      if (!response.ok || added.status) throw new Error(added.message || 'Could not add mug');

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = cartResponse.ok ? await cartResponse.json() : undefined;

      this.#trackUpsellAdd(card);
      this.#statusText = 'Mug added to cart';
      document.dispatchEvent(
        new CartAddEvent(cart, variantId, {
          source: 'cart-drawer-upsell',
          itemCount: cart?.item_count || 1,
          variantId,
          sections: added.sections,
        })
      );
    } catch (error) {
      console.error('Cart drawer upsell add failed', error);
      this.#statusText = error instanceof Error ? error.message : 'Could not add mug';
      button.disabled = false;
      button.textContent = originalText;
    }
  };

  /** @param {HTMLElement} card */
  #trackUpsellAdd(card) {
    if (typeof window.fyiLiquidTrack !== 'function') return;

    window.fyiLiquidTrack('upsell_add', {
      design_id8: card.dataset.designId8 || null,
      from_product: card.dataset.fromProduct || null,
    });
  }

  /** @param {string} value */
  set #statusText(value) {
    const status = this.querySelector('[data-upsell-status]');
    if (status) status.textContent = value;
  }

  get #cartLines() {
    const script = this.querySelector('[data-upsell-lines]');
    if (!script?.textContent) return [];

    try {
      const lines = JSON.parse(script.textContent);
      return Array.isArray(lines) ? lines : [];
    } catch (_error) {
      return [];
    }
  }

  get #sourceLines() {
    return this.#cartLines.filter((line) => (line.product_type || '').toString().trim().toLowerCase() !== 'mug');
  }

  get #cartSectionIds() {
    const sectionIds = new Set();
    document.querySelectorAll('cart-items-component').forEach((item) => {
      if (item instanceof HTMLElement && item.dataset.sectionId) sectionIds.add(item.dataset.sectionId);
    });
    if (this.dataset.sectionId) sectionIds.add(this.dataset.sectionId);
    return Array.from(sectionIds);
  }

  get #card() {
    return /** @type {HTMLElement | null} */ (this.querySelector('[data-upsell-card]'));
  }
}

if (!customElements.get('cart-drawer-upsell')) {
  customElements.define('cart-drawer-upsell', CartDrawerUpsell);
}

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
